import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Box
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

interface ConfirmationDialogProps {
  open: boolean;
  title: string;
  content: string | React.ReactNode;
  confirmButtonText?: string;
  cancelButtonText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  severity?: 'warning' | 'error' | 'info';
  confirmButtonColor?: 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  open,
  title,
  content,
  confirmButtonText = 'Confirm',
  cancelButtonText = 'Cancel',
  onConfirm,
  onCancel,
  severity = 'warning',
  confirmButtonColor
}) => {
  // Determine color based on severity
  const getColor = () => {
    switch (severity) {
      case 'error':
        return 'error';
      case 'info':
        return 'info';
      case 'warning':
      default:
        return 'warning';
    }
  };

  // Get icon based on severity
  const getIcon = () => {
    switch (severity) {
      case 'error':
      case 'warning':
      case 'info':
      default:
        return <WarningAmberIcon color={getColor()} sx={{ fontSize: '2rem' }} />;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        pb: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {getIcon()}
          <Typography variant="h6" component="span" sx={{ ml: 1.5 }}>
            {title}
          </Typography>
        </Box>
        <IconButton
          edge="end"
          color="inherit"
          onClick={onCancel}
          aria-label="close"
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {typeof content === 'string' ? (
          <Typography sx={{ whiteSpace: 'pre-line' }} variant="body1">{content}</Typography>
        ) : (
          content
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button 
          onClick={onCancel}
          variant="outlined"
          sx={{ 
            textTransform: 'none',
            minWidth: '80px',
            fontWeight: 600
          }}
        >
          {cancelButtonText}
        </Button>
        <Button 
          onClick={onConfirm}
          variant="contained"
          color={confirmButtonColor || getColor()}
          sx={{ 
            textTransform: 'none',
            minWidth: '80px',
            fontWeight: 600
          }}
        >
          {confirmButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmationDialog;